import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'motion/react';
import { 
  Search, 
  X, 
  Cpu, 
  GraduationCap, 
  Briefcase, 
  Zap, 
  MessageSquare, 
  FileText, 
  Home, 
  User, 
  Mail, 
  Terminal,
  ChevronRight,
  Sparkles,
  ShieldCheck,
  CornerDownLeft,
  Settings,
  Globe
} from 'lucide-react';

interface SearchItem {
  id: string;
  title: string;
  category: 'Page' | 'Skill' | 'Certification' | 'AI App' | 'Portfolio Project' | 'Project Showcase' | 'Website' | 'Testimonial';
  description: string;
  tab: string;
  subtext?: string;
  action?: () => void;
}

interface SiteSearchProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTab: (tabId: string) => void;
  setSelectedSkillIndex: (index: number) => void;
  setActiveSkillItem: (item: string | null) => void;
  setExpandedCerts: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  setSelectedWebIndex: (index: number) => void;
  setSelectedRecIndex: React.Dispatch<React.SetStateAction<number>>;
  onOpenPrivacy?: () => void;
  onOpenCookiePolicy?: () => void;
  
  // Static data passed down for indexing
  skills: any[];
  skillDescriptions: Record<string, string>;
  certifications: any[];
  aiApps: any[];
  portfolioProjects: any[];
  recommendations: any[];
}

export function SiteSearch({
  isOpen,
  onClose,
  onSelectTab,
  setSelectedSkillIndex,
  setActiveSkillItem,
  setExpandedCerts,
  setSelectedWebIndex,
  setSelectedRecIndex,
  onOpenPrivacy,
  onOpenCookiePolicy,
  skills,
  skillDescriptions,
  certifications,
  aiApps,
  portfolioProjects,
  recommendations
}: SiteSearchProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Construct search index dynamically based on imported data
  const searchIndex = useMemo<SearchItem[]>(() => {
    const items: SearchItem[] = [
      // Pages
      { id: 'page-home', title: 'Home Page', category: 'Page', description: 'Main landing view with IT leadership highlights and weather info.', tab: 'home' },
      { id: 'page-about', title: 'About Me', category: 'Page', description: 'Randon\'s personal story of resilience, core values, and background.', tab: 'about' },
      { id: 'page-resume', title: 'Experience', category: 'Page', description: 'Full professional history, certifications, and credentials.', tab: 'resume' },
      { id: 'page-services', title: 'Services Offered', category: 'Page', description: 'Direct on-site and pick-up IT solutions, logo design, brand identity, and systems integration.', tab: 'services' },
      { id: 'page-prices', title: 'Prices & Service Rates', category: 'Page', description: 'Transparent service rates: $37.50/30m Remote Support, $75/hr In-Shop Bench & Hometown Licking (10-mile radius), $100/hr Texas County On-Site, $150/hr Regional, $200/hr Network Engineering, and Custom Website builds.', tab: 'prices', subtext: 'Basic Hourly Rates & Support Options' },
      { id: 'page-estimator', title: 'Price Estimator', category: 'Page', description: 'Interactive job time & cost calculator for PC repairs, Wi-Fi setups, virus cleanup, phone screen repair & data migrations, hardware upgrades, and business IT.', tab: 'estimator', subtext: 'Interactive Job Time & Cost Calculator' },
      { id: 'service-websites-pricing', title: 'Website Builds (Starting at $2,000)', category: 'Page', description: 'Custom, easy-to-use website builds with mobile-friendly design, search optimization, and contact forms starting at $2,000.', tab: 'prices', subtext: 'Starting at $2,000 • Custom Web Platform' },
      { id: 'service-advanced-consulting', title: 'Network Engineering ($200/hr)', category: 'Page', description: 'Complex network builds, infrastructure engineering, server rebuilds, structured data wiring / cabling, and enterprise systems at $200 per hour.', tab: 'prices', subtext: '$200/hr • Network Engineering' },
      { id: 'service-dns-filtering', title: 'DNS Website Filtering / Family Internet Controls', category: 'Page', description: 'Configure router-level or device-level DNS content filtering, adult site blocking, ad defense, and parental screen time controls on Hometown, County, and Regional routes.', tab: 'prices', subtext: 'Family Controls & DNS Security' },
      { id: 'service-software-installs', title: 'Software Installs & Configuration', category: 'Page', description: 'Install, update, and configure desktop software, business applications, antivirus, specialized software, and system tools across all service tiers.', tab: 'prices', subtext: 'Included in All Support Tiers' },
      { id: 'page-service-quote', title: 'Request an Estimate / Quote (Need something fixed?)', category: 'Page', description: 'Technical diagnostic estimate request form for PC repairs, network issues, and consulting projects.', tab: 'service-quote' },
      { id: 'page-expertise', title: 'Expertise and Technical Skills', category: 'Page', description: 'Comprehensive directory of skills across hardware, networking, virtualization, and cloud platforms.', tab: 'expertise' },
      { id: 'service-logo-design', title: 'Logo & Brand Identity Design', category: 'Page', description: 'Custom vector logos, visual branding marks, modern typography, and digital asset packages.', tab: 'services' },
      { id: 'page-portfolio', title: 'Project Showcase', category: 'Page', description: 'Featured infrastructure projects, municipal fiber networks, and school system overhauls.', tab: 'portfolio' },
      { id: 'page-websites', title: 'Websites', category: 'Page', description: 'Explore local city websites, school district hubs, alumni communities, and business platforms built by Randon.', tab: 'websites' },
      { id: 'page-books', title: 'Books - A Boy and His Brother', category: 'Page', description: 'Children\'s picture book written & illustrated by Randon Dean Brown celebrating siblinghood and family.', tab: 'books', subtext: 'Amazon Bestseller • Illustrated Book' },
      { id: 'page-website-onboarding', title: 'Website Setup & Onboarding Guide', category: 'Page', description: 'Step-by-step onboarding protocol, domain setup, hosting & editing preferences, asset handoff, and project timeline.', tab: 'website-onboarding' },
      { id: 'page-apps', title: 'AI Applications', category: 'Page', description: 'Portfolio of custom-engineered smart apps and full-stack utilities.', tab: 'apps' },
      { id: 'page-contact', title: 'Contact Details', category: 'Page', description: 'Direct methods to securely get in touch with Randon Brown.', tab: 'contact' },
      { id: 'page-terms', title: 'Terms & Conditions', category: 'Page', description: 'Standard service agreements, diagnostic fees, payment policies, data backup liability, $200/hr network engineering terms, and hardware procurement.', tab: 'terms' },
      { 
        id: 'portal-customer', 
        title: 'Client Portal (Invoicing & Estimates)', 
        category: 'Page', 
        description: 'In-app secure client portal to view project invoices, statement history, and review estimates.', 
        tab: 'portal',
        subtext: 'In-App Account & Billing Portal'
      },
      { 
        id: 'policy-privacy', 
        title: 'Privacy Policy', 
        category: 'Page', 
        description: 'Read our privacy commitment: zero data collection, no personal tracking, and clean browsing.', 
        tab: 'about',
        action: () => {
          if (onOpenPrivacy) onOpenPrivacy();
        }
      },
      { 
        id: 'policy-cookie', 
        title: 'Cookie Policy & Consent', 
        category: 'Page', 
        description: 'Review cookie practices: strictly essential & preferences only, zero advertising trackers.', 
        tab: 'about',
        action: () => {
          if (onOpenCookiePolicy) onOpenCookiePolicy();
        }
      },
    ];

    // Add Skills
    skills.forEach((cat, catIdx) => {
      cat.items.forEach((item: string) => {
        items.push({
          id: `skill-${item.toLowerCase().replace(/[^a-z0-9]/g, '-')}`,
          title: item,
          category: 'Skill',
          description: skillDescriptions[item] || 'Broad operational experience with this hardware/software setup.',
          tab: 'services',
          subtext: cat.category,
          action: () => {
            setSelectedSkillIndex(catIdx);
            setActiveSkillItem(item);
          }
        });
      });
    });

    // Add Certifications
    certifications.forEach((cert, certIdx) => {
      items.push({
        id: `cert-${certIdx}`,
        title: cert.name,
        category: 'Certification',
        description: cert.desc,
        tab: 'resume',
        action: () => {
          setExpandedCerts(prev => ({ ...prev, [certIdx]: true }));
        }
      });
    });

    // Add AI Apps
    aiApps.forEach((app, appIdx) => {
      items.push({
        id: `app-${appIdx}`,
        title: app.name,
        category: 'AI App',
        description: app.description,
        tab: 'apps',
        subtext: app.features.join(', ')
      });
    });

    // Add Showcase Projects & Websites
    portfolioProjects.forEach((proj, projIdx) => {
      const isWebsite = !proj.isFlagship && Boolean(proj.url);
      items.push({
        id: `proj-${projIdx}`,
        title: proj.name,
        category: isWebsite ? 'Website' : 'Project Showcase',
        description: proj.description,
        tab: isWebsite ? 'websites' : 'portfolio',
        action: () => {
          if (!isWebsite && typeof setSelectedWebIndex === 'function') {
            setSelectedWebIndex(projIdx);
          }
        }
      });
    });

    // Add Testimonials
    recommendations.forEach((rec, recIdx) => {
      items.push({
        id: `rec-${recIdx}`,
        title: rec.name,
        category: 'Testimonial',
        description: rec.excerpt,
        tab: 'home',
        subtext: `${rec.role} at ${rec.org}`,
        action: () => {
          setSelectedRecIndex(recIdx);
          setTimeout(() => {
            const el = document.getElementById("home-testimonials");
            if (el) el.scrollIntoView({ behavior: 'smooth' });
          }, 150);
        }
      });
    });

    return items;
  }, [skills, skillDescriptions, certifications, aiApps, portfolioProjects, recommendations]);

  // Filter search items
  const filteredItems = useMemo(() => {
    if (!query.trim()) return searchIndex;
    const cleanQuery = query.toLowerCase().trim();
    return searchIndex.filter(item => 
      item.title.toLowerCase().includes(cleanQuery) ||
      item.description.toLowerCase().includes(cleanQuery) ||
      item.category.toLowerCase().includes(cleanQuery) ||
      (item.subtext && item.subtext.toLowerCase().includes(cleanQuery))
    );
  }, [query, searchIndex]);

  // Reset selection index when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % Math.max(1, filteredItems.length));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + filteredItems.length) % Math.max(1, filteredItems.length));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredItems[selectedIndex]) {
          handleSelect(filteredItems[selectedIndex]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredItems, selectedIndex]);

  // Scroll into view helper
  useEffect(() => {
    const selectedElement = resultsContainerRef.current?.children[selectedIndex] as HTMLElement;
    if (selectedElement) {
      selectedElement.scrollIntoView({
        block: 'nearest',
      });
    }
  }, [selectedIndex]);

  const handleSelect = (item: SearchItem) => {
    // Switch to target tab
    onSelectTab(item.tab);
    
    // Execute custom item actions
    if (item.action) {
      item.action();
    }

    onClose();
  };

  const getCategoryIcon = (category: SearchItem['category']) => {
    switch (category) {
      case 'Page':
        return <Home className="w-4 h-4 text-brand-400" />;
      case 'Skill':
        return <Cpu className="w-4 h-4 text-emerald-400" />;
      case 'Certification':
        return <GraduationCap className="w-4 h-4 text-blue-400" />;
      case 'AI App':
        return <Zap className="w-4 h-4 text-amber-400" />;
      case 'Portfolio Project':
      case 'Project Showcase':
        return <Briefcase className="w-4 h-4 text-purple-400" />;
      case 'Website':
        return <Globe className="w-4 h-4 text-cyan-400" />;
      case 'Testimonial':
        return <MessageSquare className="w-4 h-4 text-rose-400" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 pt-[10vh] overflow-y-auto">
      {/* Backdrop */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Spotlight Search Box Container */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.96, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: -10 }}
        transition={{ type: 'spring', damping: 25, stiffness: 350 }}
        className="relative w-full max-w-2xl bg-[#0c0c0c] border border-brand-500/25 rounded-2xl shadow-[0_0_50px_rgba(245,158,11,0.15)] overflow-hidden flex flex-col max-h-[70vh] z-10"
      >
        {/* Search Input Area */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-neutral-900 bg-[#090909]">
          <Search className="w-5 h-5 text-slate-500 shrink-0" />
          <input 
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search skills, certs, projects, testimonials..."
            className="flex-1 bg-transparent text-sm text-slate-100 placeholder-slate-500 focus:outline-none border-none ring-0 w-full"
          />
          <div className="flex items-center gap-1">
            <span className="hidden sm:inline-block text-[10px] font-mono text-slate-500 bg-neutral-900 border border-neutral-800 px-1.5 py-0.5 rounded">ESC</span>
            <button 
              onClick={onClose}
              className="p-1 text-slate-500 hover:text-white hover:bg-neutral-900 rounded-lg transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Results Body */}
        <div 
          ref={resultsContainerRef}
          className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-0.5 max-h-[45vh]"
        >
          {filteredItems.length === 0 ? (
            <div className="py-12 text-center text-slate-500 flex flex-col items-center justify-center gap-3">
              <Sparkles className="w-8 h-8 text-neutral-800 animate-pulse" />
              <div className="space-y-1">
                <p className="text-sm font-semibold text-slate-400">No results found</p>
                <p className="text-xs text-slate-600">No matches for "{query}" on this site.</p>
              </div>
            </div>
          ) : (
            filteredItems.map((item, index) => {
              const isSelected = index === selectedIndex;
              return (
                <div
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={`group flex items-start gap-3.5 px-3.5 py-3 rounded-xl transition-all duration-150 cursor-pointer text-left select-none ${
                    isSelected 
                      ? 'bg-brand-500/10 border-l-2 border-brand-500 shadow-[inset_1px_0_0_rgba(245,158,11,0.15)]' 
                      : 'hover:bg-white/[0.01] border-l-2 border-transparent'
                  }`}
                >
                  <div className={`p-2 rounded-lg shrink-0 transition-colors ${
                    isSelected ? 'bg-brand-500/15 text-brand-400' : 'bg-neutral-900 text-slate-400 group-hover:bg-neutral-800'
                  }`}>
                    {getCategoryIcon(item.category)}
                  </div>
                  
                  <div className="flex-1 min-w-0 space-y-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-bold leading-none transition-colors ${
                        isSelected ? 'text-brand-400' : 'text-slate-200'
                      }`}>
                        {item.title}
                      </span>
                      <span className="text-[9px] font-mono font-bold uppercase px-1.5 py-0.5 rounded bg-neutral-900/80 text-slate-500 border border-neutral-900">
                        {item.category}
                      </span>
                      {item.subtext && (
                        <span className="text-[10px] text-slate-500 font-mono">
                          &bull; {item.subtext}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-400 leading-normal line-clamp-2">
                      {item.description}
                    </p>
                  </div>

                  {isSelected && (
                    <div className="self-center flex items-center text-[10px] font-mono text-brand-400 shrink-0 animate-pulse">
                      <span className="mr-1">Go</span>
                      <CornerDownLeft className="w-3 h-3" />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Search Help Info Footer */}
        <div className="px-4 py-2 bg-[#090909] border-t border-neutral-900 flex items-center justify-between text-[10px] font-mono text-slate-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <span className="bg-neutral-900 border border-neutral-800 px-1 rounded">↑</span>
              <span className="bg-neutral-900 border border-neutral-800 px-1 rounded">↓</span>
              to navigate
            </span>
            <span className="flex items-center gap-1">
              <span className="bg-neutral-900 border border-neutral-800 px-1.5 py-0.5 rounded">Enter</span>
              to select
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span>Found {filteredItems.length} matches</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
